import { useMemo } from 'react';
import { Box, Typography, Tooltip, IconButton, Stack } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { HelpCircle, Moon, Sun } from 'lucide-react';
import { design } from '../../theme/clinveaDesign';

export interface AppTopBarProps {
  mode: 'light' | 'dark' | string | undefined;
  onToggleColorMode: () => void;
  onHelp: () => void;
  /** Route path → label, so the crumb names the screen the user is actually on. */
  navLabels: Map<string, string>;
  /** Rendered between the crumb and the utilities: search, branch switcher, bell, AI chat. */
  children?: React.ReactNode;
  /** Clinic session line, e.g. "OPD 1 · 09:00–14:00". Hidden when not supplied. */
  shiftLabel?: string | null;
}

/**
 * The application top bar.
 *
 * <p>This deliberately reverses an earlier decision. The top bar had been removed application-wide
 * and its controls (search, branch switcher, notifications, AI chat, theme) moved into a utility
 * strip at the bottom of the sidebar. The approved design reinstates it, so those controls move
 * back up here — they are passed in as `children` rather than re-implemented, and the sidebar strip
 * is reduced to the collapse toggle, so there is still exactly one instance of each control.
 */
export function AppTopBar({
  mode, onToggleColorMode, onHelp, navLabels, children, shiftLabel,
}: AppTopBarProps) {
  const d = design(mode);
  const location = useLocation();

  // Longest matching nav path wins, so /admin/fee-schedule resolves to the fee-schedule entry
  // rather than to /admin. Falls back to nothing rather than guessing a label from the URL.
  const crumb = useMemo(() => {
    let best: string | null = null;
    let bestLen = -1;
    navLabels.forEach((label, path) => {
      if ((location.pathname === path || location.pathname.startsWith(path + '/')) && path.length > bestLen) {
        best = label;
        bestLen = path.length;
      }
    });
    return best;
  }, [location.pathname, navLabels]);

  return (
    <Box
      component="header"
      sx={{
        flexShrink: 0,
        height: 56,
        bgcolor: d.surface,
        borderBottom: '1px solid',
        borderColor: d.line,
        display: 'flex',
        alignItems: 'center',
        gap: 1.75,
        px: 2.25,
      }}
    >
      {crumb && (
        <Typography sx={{ fontSize: '0.82rem', color: d.faint, flexShrink: 0 }} noWrap>
          <Box component="span" sx={{ color: d.ink, fontWeight: 600 }}>{crumb}</Box>
        </Typography>
      )}

      {/* Search and the other utilities, supplied by Layout so this component owns no state. */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
        {children}
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
        {shiftLabel && (
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.9,
              fontSize: '0.76rem', color: d.muted,
              border: '1px solid', borderColor: d.line, borderRadius: '8px',
              px: 1.4, py: 0.85, bgcolor: d.surface, whiteSpace: 'nowrap',
            }}
          >
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: d.green, flexShrink: 0 }} />
            {shiftLabel}
          </Box>
        )}

        <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
          <IconButton
            onClick={onToggleColorMode}
            size="small"
            sx={{ border: '1px solid', borderColor: d.line, borderRadius: '8px', color: d.muted, width: 34, height: 34 }}
          >
            {mode === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Help">
          <IconButton
            onClick={onHelp}
            size="small"
            sx={{ border: '1px solid', borderColor: d.line, borderRadius: '8px', color: d.muted, width: 34, height: 34 }}
          >
            <HelpCircle size={16} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
}
